// src/views/sops/SopLibrary.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import { tokens } from "../../theme/tokens";
import { INSTANTIATE_SOP } from "../../graph/projects/mutations";
import {
  READ_PROJECTS_FILTER,
  READ_SOP_LIBRARY_FILTER_DATA,
} from "../../graph/projects/queries";
import { graphqlEndpoint } from "../../config";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useTenant } from "../../context/TenantContext";
import CompactPreview from "../../components/CompactPreview";
import TableActions from "../../components/TableActions";

const client = new GraphQLClient(graphqlEndpoint);

export default function SopLibrary() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { setSnackbar } = useSnackbar();
  const { tenantId, tenantName } = useTenant();

  const [sops, setSops] = useState([]);

  const [instOpen, setInstOpen] = useState(false);
  const [instSopId, setInstSopId] = useState(null);
  const [instSopTitle, setInstSopTitle] = useState("");
  const [instanceName, setInstanceName] = useState("");

  const [filterData, setFilterData] = useState(null);
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");

  useEffect(() => {
    const fetchSops = async () => {
      if (!tenantId) {
        setSops([]);
        return;
      }

      try {
        const { readProjectsFilter } = await client.request(
          READ_PROJECTS_FILTER,
          {
            filter: {
              tenantId,
              isSOPTemplate: true,
            },
          }
        );

        setSops(readProjectsFilter || []);
      } catch (error) {
        console.error("Failed to load SOPs:", error);

        setSops([]);

        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load SOP library",
        });
      }
    };

    fetchSops();
  }, [tenantId, setSnackbar]);

  useEffect(() => {
    const fetchFilterData = async () => {
      if (!tenantId) {
        setFilterData(null);
        return;
      }

      try {
        const { readRoleAllocationBoard: sopLibraryFilterData } =
          await client.request(READ_SOP_LIBRARY_FILTER_DATA, {
            input: {
              tenantId,
            },
          });

        console.log("SOP library filter data", sopLibraryFilterData);

        setFilterData(sopLibraryFilterData);
      } catch (error) {
        console.error("Failed to load SOP filter data:", error);

        setFilterData(null);
      }
    };

    fetchFilterData();
  }, [tenantId]);

  const openInst = (sop) => {
    setInstSopId(sop.id);
    setInstSopTitle(sop.title);
    setInstanceName("");
    setInstOpen(true);
  };

  const closeInst = () => {
    setInstOpen(false);
    setInstSopId(null);
    setInstSopTitle("");
    setInstanceName("");
  };

  const roleItems = filterData?.roles || [];
  const departments = filterData?.departments || [];
  const roles = roleItems.map((item) => item.role).filter(Boolean);

  const filteredSops = sops.filter((sop) => {
    const matchesDepartment =
      departmentFilter === "all" ||
      roleItems.some(
        (roleItem) =>
          roleItem.role?.departmentId === departmentFilter &&
          roleItem.sopCards?.some((card) => card.sop?.id === sop.id)
      );

    const matchesRole =
      roleFilter === "all" ||
      roleItems.some(
        (roleItem) =>
          roleItem.role?.id === roleFilter &&
          roleItem.sopCards?.some((card) => card.sop?.id === sop.id)
      );

    return matchesDepartment && matchesRole;
  });

  const submitInst = async () => {
    if (!instSopId || !instanceName.trim()) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Please enter a name",
      });

      return;
    }

    try {
      const { instantiateSOP } = await client.request(INSTANTIATE_SOP, {
        input: {
          id: instSopId,
          instanceName: instanceName.trim(),
          copyStatus: false,
        },
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Project instance created",
      });

      closeInst();

      if (instantiateSOP?.id) {
        navigate(`/projects/edit/${instantiateSOP.id}`, {
          state: {
            redirectPath: "/manageProjects",
          },
        });
      } else {
        navigate("/manageProjects");
      }
    } catch (error) {
      console.error("Instantiate failed:", error);

      const message =
        error?.response?.errors?.[0]?.message ||
        "Failed to instantiate SOP";

      setSnackbar({
        open: true,
        severity: "error",
        message,
      });
    }
  };

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="SOP LIBRARY"
          subtitle="Select a tenant before viewing SOP templates"
        />

        <Typography mt={3}>
          Use the tenant selector in the top bar, then return here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        mb={2}
      >
        <Header
          title="SOP LIBRARY"
          subtitle={
            tenantName
              ? `Standard operating procedures for ${tenantName}`
              : "Standard operating procedures"
          }
        />
      </Box>

      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          select
          size="small"
          label="Department"
          value={departmentFilter}
          onChange={(event) => setDepartmentFilter(event.target.value)}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="all">All departments</MenuItem>

          {departments.map((department) => (
            <MenuItem key={department.id} value={department.id}>
              {department.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Role"
          value={roleFilter}
          onChange={(event) => setRoleFilter(event.target.value)}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="all">All roles</MenuItem>

          {roles.map((role) => (
            <MenuItem key={role.id} value={role.id}>
              {role.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box
        display="grid"
        gap={2}
        gridTemplateColumns="repeat(auto-fill, minmax(320px, 1fr))"
      >
        {filteredSops.map((sop) => {
          const total = sop.tasks?.length || 0;

          const done = (sop.tasks || []).filter(
            (task) => task.status === "DONE"
          ).length;

          const progressPercentage = total
            ? Math.round((done / total) * 100)
            : 0;

          return (
            <Paper
              key={sop.id}
              sx={{
                p: 2,
                border: `1px solid ${colors.scalpelTeal[400]}`,
                bgcolor: "background.paper",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 0.5,
                  fontWeight: 700,
                  color: colors.scalpelTeal[400],
                }}
              >
                {sop.title}
              </Typography>

              {sop.sopVersion ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    mb: 1,
                  }}
                >
                  Version: {sop.sopVersion}
                </Typography>
              ) : null}

              {sop.labels?.length ? (
                <Box
                  sx={{
                    mb: 1,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                  }}
                >
                  {sop.labels.map((label) => (
                    <Chip key={label} size="small" label={label} />
                  ))}
                </Box>
              ) : null}

              <Typography
                variant="body2"
                sx={{ mb: 1.5 }}
                color="text.secondary"
              >
              <CompactPreview lines={8}>
                {sop.description || "No description"}
              </CompactPreview>
              </Typography>

              <Box sx={{ mb: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                />

                <Typography variant="caption">
                  {done}/{total} steps completed
                </Typography>
              </Box>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={1}
                gap={1}
              >
                <Chip size="small" label={sop.status} />

                <TableActions
                  onView={() => navigate(`/sops/read/${sop.id}`)}
                  onInstantiate={() => openInst(sop)}
                  hideCreate
                />
              </Box>
            </Paper>
          );
        })}
      </Box>

      <Dialog
        open={instOpen}
        onClose={closeInst}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Instantiate “{instSopTitle}”</DialogTitle>

        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Instance name"
            value={instanceName}
            onChange={(event) => setInstanceName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                submitInst();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={closeInst}>Cancel</Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={submitInst}
          >
            Create Project
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}